/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { fetchGraphQl } from '@/pdp/api';
import { ProductModel } from '@/pdp/data/models';
import { transformProductData } from '@/pdp/data/transforms';
import { GET_PRODUCTS_DATA } from './graphql/getProductsData.graphql';

export interface ProductDataItem {
  sku: string;
  optionsUIDs?: string[];
  preselectFirstOption?: boolean;
}

/**
 * Fetches multiple products by SKUs with optional per-product configuration.
 *
 * @example
 * // Basic usage
 * const products = await getProductsData([
 *   { sku: 'SKU001' },
 *   { sku: 'SKU002' }
 * ]);
 *
 * @example
 * // With options
 * const products = await getProductsData([
 *   { sku: 'SKU001', optionsUIDs: ['option1'], preselectFirstOption: true },
 *   { sku: 'SKU002', optionsUIDs: ['option2'] }
 * ]);
 *
 * @example
 * // Get raw data without transformation
 * const rawProducts = await getProductsData([{ sku: 'SKU001' }], true);
 */
export const getProductsData = async (
  items: ProductDataItem[],
  raw?: boolean
): Promise<ProductModel[] | null> => {
  if (!items || items.length === 0) {
    return null;
  }

  const skus = items.map((item) => item.sku);
  const { data } = await fetchGraphQl(GET_PRODUCTS_DATA, {
    method: 'GET',
    variables: { skus },
  });
  const products = data?.products;

  if (!products || products.length === 0) {
    return null;
  }

  // Create a map for quick lookup of options by SKU
  const optionsMap = items.reduce((acc, item) => {
    acc[item.sku] = item;
    return acc;
  }, {} as Record<string, ProductDataItem>);

  return raw
    ? products
    : products.map((product: any) => {
        const itemOptions = optionsMap[product.sku];

        // If optionsUIDs are provided, override the optionsUIDs from the response
        if (itemOptions?.optionsUIDs) {
          product.optionUIDs = itemOptions.optionsUIDs;
        }

        return transformProductData(product, {
          preselectFirstOption: itemOptions?.preselectFirstOption,
        });
      });
};
